import { useCallback, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';

// =============================================================================
// CELEBRATION TYPES
// =============================================================================

export type CelebrationType =
  | 'confetti'      // ATH ou target atteinte
  | 'rocket'        // +10% gain
  | 'fireworks'     // Gros milestone
  | 'coins'         // Transaction réussie
  | 'stars';        // Achievement unlock

export interface CelebrationOptions {
  duration?: number;
  intensity?: 'low' | 'medium' | 'high';
  position?: { x: number; y: number };
}

// =============================================================================
// CONFETTI PRESETS
// =============================================================================

const confettiDefaults = {
  spread: 360,
  ticks: 100,
  gravity: 0.8,
  decay: 0.94,
  startVelocity: 30,
  colors: ['#FFD700', '#FFA500', '#FF6347', '#00CED1', '#9370DB', '#3CB371'],
};

// Confetti classique pour ATH/Target
export const fireConfetti = (options?: CelebrationOptions) => {
  const intensity = options?.intensity || 'medium';
  const particleCount = intensity === 'low' ? 50 : intensity === 'medium' ? 100 : 200;

  const origin = options?.position
    ? { x: options.position.x / window.innerWidth, y: options.position.y / window.innerHeight }
    : { x: 0.5, y: 0.6 };

  // Burst principal
  confetti({
    ...confettiDefaults,
    particleCount,
    origin,
  });

  // Second burst avec délai pour effet cascade
  setTimeout(() => {
    confetti({
      ...confettiDefaults,
      particleCount: particleCount * 0.5,
      origin: { x: origin.x - 0.1, y: origin.y },
    });
    confetti({
      ...confettiDefaults,
      particleCount: particleCount * 0.5,
      origin: { x: origin.x + 0.1, y: origin.y },
    });
  }, 150);
};

// Feux d'artifice pour gros milestones
export const fireFireworks = (duration: number = 3000) => {
  const animationEnd = Date.now() + duration;

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }

    // Tirs aléatoires des deux côtés
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#FFD700', '#FFA500', '#FF6347'],
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#00CED1', '#9370DB', '#3CB371'],
    });
  }, 100);
};

// Pluie de pièces pour transactions
export const fireCoins = (options?: CelebrationOptions) => {
  const origin = options?.position
    ? { x: options.position.x / window.innerWidth, y: options.position.y / window.innerHeight }
    : { x: 0.5, y: 0.3 };

  // Simulation de pièces dorées
  confetti({
    particleCount: 50,
    spread: 60,
    origin,
    colors: ['#FFD700', '#FFC700', '#FFAA00', '#DAA520'],
    shapes: ['circle'],
    gravity: 1.2,
    scalar: 1.2,
    ticks: 150,
  });
};

// Étoiles pour achievements
export const fireStars = (options?: CelebrationOptions) => {
  const origin = options?.position
    ? { x: options.position.x / window.innerWidth, y: options.position.y / window.innerHeight }
    : { x: 0.5, y: 0.5 };

  confetti({
    particleCount: 30,
    spread: 360,
    origin,
    colors: ['#FFD700', '#FFFFFF', '#87CEEB'],
    shapes: ['star'],
    gravity: 0.5,
    scalar: 1.5,
    ticks: 200,
    startVelocity: 20,
  });
};

// =============================================================================
// ROCKET CELEBRATION COMPONENT
// =============================================================================

interface RocketCelebrationProps {
  show: boolean;
  onComplete?: () => void;
  message?: string;
}

export const RocketCelebration: React.FC<RocketCelebrationProps> = ({
  show,
  onComplete,
  message = '+10% 🚀'
}) => {
  useEffect(() => {
    if (show && onComplete) {
      const timer = setTimeout(onComplete, 4500);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="rocket-celebration-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Rocket */}
          <motion.div
            initial={{ y: '100vh', rotate: 0 }}
            animate={{
              y: '-100vh',
              rotate: [0, -5, 5, -3, 3, 0],
            }}
            transition={{
              y: { duration: 4, ease: [0.25, 0.46, 0.45, 0.94] },
              rotate: { duration: 0.5, repeat: 8 }
            }}
            style={{
              fontSize: '4rem',
              filter: 'drop-shadow(0 0 20px rgba(255, 165, 0, 0.8))',
            }}
          >
            🚀
          </motion.div>

          {/* Trail effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 100 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.5, 1.5, 2], y: [100, 0, 0, -50] }}
            transition={{ duration: 3.5, delay: 0.2 }}
            style={{
              position: 'absolute',
              fontSize: '2rem',
              color: '#FFD700',
              fontWeight: 'bold',
              textShadow: '0 0 20px rgba(255, 215, 0, 0.8)',
            }}
          >
            {message}
          </motion.div>

          {/* Particle trail */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 0,
                y: 0,
                x: (Math.random() - 0.5) * 100,
              }}
              animate={{
                opacity: [0, 1, 0],
                y: [0, 200 + Math.random() * 100],
                scale: [1, 0.5],
              }}
              transition={{
                duration: 2.5,
                delay: 0.15 * i,
                ease: 'easeOut',
              }}
              style={{
                position: 'absolute',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${['#FFD700', '#FFA500', '#FF6347'][i % 3]} 0%, transparent 70%)`,
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// ATH CELEBRATION COMPONENT
// =============================================================================

interface ATHCelebrationProps {
  show: boolean;
  onComplete?: () => void;
  tokenSymbol?: string;
  newATH?: string;
}

export const ATHCelebration: React.FC<ATHCelebrationProps> = ({
  show,
  onComplete,
  tokenSymbol = 'Portfolio',
  newATH,
}) => {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (show && !hasTriggered.current) {
      hasTriggered.current = true;

      // Fire confetti burst
      fireConfetti({ intensity: 'high' });

      // Fire secondary bursts
      setTimeout(() => fireConfetti({ intensity: 'medium' }), 300);
      setTimeout(() => fireFireworks(2000), 500);

      if (onComplete) {
        const timer = setTimeout(onComplete, 4000);
        return () => clearTimeout(timer);
      }
    }

    if (!show) {
      hasTriggered.current = false;
    }
  }, [show, onComplete]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="ath-celebration-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* ATH Badge */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
            }}
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
              borderRadius: 20,
              padding: '24px 48px',
              boxShadow: '0 0 60px rgba(255, 215, 0, 0.6), 0 0 100px rgba(255, 165, 0, 0.4)',
            }}
          >
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
              style={{
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: 8 }}>👑</div>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1a1a1a',
                letterSpacing: '0.1em',
              }}>
                NEW ATH!
              </div>
              <div style={{
                fontSize: '1rem',
                color: '#333',
                marginTop: 4,
              }}>
                {tokenSymbol}
              </div>
              {newATH && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    marginTop: 8,
                  }}
                >
                  {newATH}
                </motion.div>
              )}
            </motion.div>
          </motion.div>

          {/* Glow rings */}
          {[1, 2, 3].map((ring) => (
            <motion.div
              key={ring}
              initial={{ scale: 0.5, opacity: 0.8 }}
              animate={{ scale: 2 + ring * 0.5, opacity: 0 }}
              transition={{
                duration: 2,
                delay: ring * 0.2,
                repeat: Infinity,
                ease: 'easeOut',
              }}
              style={{
                position: 'absolute',
                width: 200,
                height: 200,
                borderRadius: '50%',
                border: '2px solid #FFD700',
                pointerEvents: 'none',
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// TARGET REACHED CELEBRATION
// =============================================================================

interface TargetReachedProps {
  show: boolean;
  onComplete?: () => void;
  targetName?: string;
  targetValue?: string;
}

export const TargetReachedCelebration: React.FC<TargetReachedProps> = ({
  show,
  onComplete,
  targetName = 'Target',
  targetValue,
}) => {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (show && !hasTriggered.current) {
      hasTriggered.current = true;

      // Confetti burst
      fireConfetti({ intensity: 'medium' });
      fireStars();

      if (onComplete) {
        const timer = setTimeout(onComplete, 3000);
        return () => clearTimeout(timer);
      }
    }

    if (!show) {
      hasTriggered.current = false;
    }
  }, [show, onComplete]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <motion.div
            initial={{ scale: 0, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0, y: -50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            style={{
              background: 'linear-gradient(135deg, #3CB371 0%, #2E8B57 100%)',
              borderRadius: 16,
              padding: '20px 40px',
              boxShadow: '0 0 40px rgba(60, 179, 113, 0.5)',
            }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              style={{ textAlign: 'center' }}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🎯</div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
              }}>
                Target Reached!
              </div>
              <div style={{ color: 'rgba(255,255,255,0.9)', marginTop: 4 }}>
                {targetName}
              </div>
              {targetValue && (
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'white',
                  marginTop: 8,
                }}>
                  {targetValue}
                </div>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// CELEBRATION HOOK
// =============================================================================

export interface UseCelebrationReturn {
  triggerConfetti: (options?: CelebrationOptions) => void;
  triggerRocket: (message?: string) => void;
  triggerATH: (tokenSymbol?: string, newATH?: string) => void;
  triggerTarget: (targetName?: string, targetValue?: string) => void;
  triggerCoins: (options?: CelebrationOptions) => void;
  triggerFireworks: (duration?: number) => void;
  CelebrationComponents: React.FC;
}

export const useCelebration = (): UseCelebrationReturn => {
  const [showRocket, setShowRocket] = useState(false);
  const [rocketMessage, setRocketMessage] = useState('+10% 🚀');
  const [showATH, setShowATH] = useState(false);
  const [athData, setAthData] = useState({ symbol: '', value: '' });
  const [showTarget, setShowTarget] = useState(false);
  const [targetData, setTargetData] = useState({ name: '', value: '' });

  const triggerConfetti = useCallback((options?: CelebrationOptions) => {
    fireConfetti(options);
  }, []);

  const triggerRocket = useCallback((message?: string) => {
    if (message) setRocketMessage(message);
    setShowRocket(true);
  }, []);

  const triggerATH = useCallback((tokenSymbol?: string, newATH?: string) => {
    setAthData({ symbol: tokenSymbol || '', value: newATH || '' });
    setShowATH(true);
  }, []);

  const triggerTarget = useCallback((targetName?: string, targetValue?: string) => {
    setTargetData({ name: targetName || '', value: targetValue || '' });
    setShowTarget(true);
  }, []);

  const triggerCoins = useCallback((options?: CelebrationOptions) => {
    fireCoins(options);
  }, []);

  const triggerFireworks = useCallback((duration?: number) => {
    fireFireworks(duration);
  }, []);

  const CelebrationComponents: React.FC = useCallback(() => (
    <>
      <RocketCelebration
        show={showRocket}
        onComplete={() => setShowRocket(false)}
        message={rocketMessage}
      />
      <ATHCelebration
        show={showATH}
        onComplete={() => setShowATH(false)}
        tokenSymbol={athData.symbol}
        newATH={athData.value}
      />
      <TargetReachedCelebration
        show={showTarget}
        onComplete={() => setShowTarget(false)}
        targetName={targetData.name}
        targetValue={targetData.value}
      />
    </>
  ), [showRocket, rocketMessage, showATH, athData, showTarget, targetData]);

  return {
    triggerConfetti,
    triggerRocket,
    triggerATH,
    triggerTarget,
    triggerCoins,
    triggerFireworks,
    CelebrationComponents,
  };
};

// Fix: Add missing import
import { useState } from 'react';

export default {
  fireConfetti,
  fireFireworks,
  fireCoins,
  fireStars,
  RocketCelebration,
  ATHCelebration,
  TargetReachedCelebration,
  useCelebration,
};
